function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-04-22 12:37:28 +0200 (Wed, 22 Apr 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 162462 $
//  Purpose   : image viewer react component
// =============================================================================
// ---Third-party imports
import React, { PureComponent, Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Collection } from 'uix/chunks';
import now from 'performance-now';
import clsx from 'clsx';
// ---Framework imports
import connect from 'uix/connect';
import { range, each, flatten, isEqual, get, sum, has, zip, cloneDeep, mergeDeep, isString, isObject, size, isNil, callIfFunc } from 'ldash';
import log from 'uix/logging';
import { getTextWidth, isSSR, hashObj, windowDevicePixelRatio, getBestWebcacheFit } from 'uix/utils';
import { useRef, useSelector, useEffect, useMemo, useCallback, useLayoutEffect, useState } from 'uix/hooks';
// ---Local imports
import { smoothScroll, runMorph, smoothScrollSeq } from './actions';
import { Layout, hashLayout } from './layouts';
import { DebugPane } from './debug';
import { CursorPosition, isMetaKey } from './cursor';
import { defaultOptions, OptionSets, validateOptions, MIN_ZOOM } from './options';
import { wcUrl, ControlledImage, ControlledImageDebug } from './image.js';
import { Layers } from './layers';
import { getPagesFlat } from './hooks';
import { AutoSizer } from 'uix/components';
import { ZoomControl } from './controls';
import styles from './viewer.css';
import { AccessWarning } from 'uix//components';
// ---Globals
const DEBUG_LIMIT = 0;
const Z_PAG_ADJUST = 0.94;
// make this a Set() ?
let TWC = {};
let getTextWidthCached = (text, font)=>{
    let key = `${font}_${text}`;
    let twc = TWC[key];
    if (!twc) {
        twc = getTextWidth(text, font);
        TWC[key] = twc;
    //~ log('cache %o %o', font, text)
    }
    return twc;
};
let _recomputes_ = 0;
let _panerrenders_ = 0;
const RenderPane = ({ list, renderArea, zoom, positionHash, renderHash, scrollLeft, scrollTop, onScrollInfo, onResizeInfo, onCursorInfo, onCursorDeactivate, onDrag, onDragStart, onDragEnd, onWheel, onRectSelect, selectMetaKey, dragScrollMetaKey, viewerId, className, layoutWidth, layoutHeight, animating })=>{
    let horizontalOverscanSize = 0;
    let verticalOverscanSize = 0;
    let oversizeIndex = list.length;
    let collectionRef = useRef();
    let cellRenderer = useCallback(({ index, isScrolling, key, style })=>{
        if (index == oversizeIndex) {
            return /*#__PURE__*/ _jsx("div", {
                id: "viewerOversizeItem"
            }, key);
        }
        let area = list[index % list.length];
        return renderArea(area, index, isScrolling, key, style);
    }, [
        oversizeIndex,
        list,
        renderArea,
        renderHash
    ]);
    let cellSizeAndPositionGetter = useCallback(({ index })=>{
        if (index == oversizeIndex) {
            return {
                height: 0,
                width: 0,
                x: layoutWidth,
                y: layoutHeight
            };
        }
        // assume all list members have {x, y, width, height} props
        return list[index % list.length];
    }, [
        oversizeIndex,
        positionHash
    ]);
    let noContentRenderer = useCallback(()=>/*#__PURE__*/ _jsx("div", {
            className: styles.noCells
        }));
    // delay scrolling operations if layout size changes
    // incoming scroll props will be invalid if layout dimensions changes (zoom changes)
    // we depend on the assumption that in the next render cycle (after our useEffect/setState update)
    // the underlying dom has the correct dimensions and the scrollLeft/scrollTop values are now valid
    // empirically: seems to be so
    let [knownLayoutWidth, setKnownLayoutWidth] = useState(0);
    useEffect(()=>{
        setKnownLayoutWidth(layoutWidth);
    }, [
        layoutWidth
    ]);
    if (layoutWidth != knownLayoutWidth) {
        scrollLeft = undefined;
        scrollTop = undefined;
    }
    useLayoutEffect(()=>{
        if (collectionRef.current) {
            collectionRef.current.recomputeCellSizesAndPositions();
            _recomputes_ += 1;
            collectionRef.current.forceUpdate();
        }
    }, [
        positionHash
    ]);
    useEffect(()=>{
        if (collectionRef.current) {
            collectionRef.current.forceUpdate();
        }
    }, [
        renderHash,
        animating
    ]);
    // we inject one additional bottom corner item with this oversizeIndex stuff
    let cellCount = oversizeIndex > 0 ? oversizeIndex + 1 : 0;
    // default is 100 - the size of the sections a Collection's cells are split into.
    // This is an advanced option and should only be used for performance tuning purposes.
    // Dirk: zoom becomes horrible slow with more than 300 items on default=100
    // adapt this to zoom: makes things fast again
    // this is purely empirical
    let sectionSize = Math.max(100, zoom * 1);
    let deps = [
        className,
        cellCount,
        cellRenderer,
        onScrollInfo,
        scrollLeft,
        scrollTop,
        onCursorInfo,
        onDrag,
        onDragStart,
        onDragEnd,
        onWheel,
        onRectSelect,
        selectMetaKey,
        dragScrollMetaKey,
        onCursorDeactivate
    ];
    //~ log.info('render %o: %o cells, zoom %o', listid, cellCount, zoom)
    return useMemo(()=>{
        _panerrenders_ += 1;
        let autoSizer = ({ width, height })=>{
            if (isSSR) {
                // we could probably use defaultWidth={800} defaultHeight={600}
                // but handling isSSR/SSR explicitly needs on less render op
                width = 800;
                height = 600;
            } else if (width == 0) return null // initial mount
            ;
            let collection = /*#__PURE__*/ _jsx(Collection, {
                positionHash: positionHash,
                renderHash: renderHash,
                cellCount: cellCount,
                cellRenderer: cellRenderer,
                cellSizeAndPositionGetter: cellSizeAndPositionGetter,
                id: `paneCollection_${viewerId}`,
                className: styles.viewerCollection,
                height: height,
                noContentRenderer: noContentRenderer,
                verticalOverscanSize: verticalOverscanSize,
                horizontalOverscanSize: horizontalOverscanSize,
                width: width,
                onScroll: onScrollInfo,
                scrollLeft: scrollLeft,
                scrollTop: scrollTop,
                sectionSize: sectionSize,
                ref: collectionRef
            });
            if (onCursorInfo) {
                // install cursor/mouse tracker
                let p = {
                    width,
                    height,
                    shouldDecorateChildren: false,
                    onPositionChanged: onCursorInfo,
                    onDrag,
                    onDragStart,
                    onDragEnd,
                    onWheel,
                    onSelect: onRectSelect,
                    selectMetaKey,
                    dragScrollMetaKey,
                    onCursorDeactivate
                };
                collection = /*#__PURE__*/ _jsx(CursorPosition, {
                    ...p,
                    children: collection
                });
            }
            return collection;
        };
        return /*#__PURE__*/ _jsx(AutoSizer, {
            className: clsx('viewerAutoSizer', className),
            onResize: onResizeInfo,
            children: autoSizer
        });
    }, deps);
};
class FreeMordor extends Component {
    static _resolveOptions(props) {
        /* merge external options into default options
     */ let opts = {
            ...defaultOptions
        };
        if (props.optionSet) {
            if (!has(OptionSets, props.optionSet)) throw Error(`unknown optionSet ${props.optionSet}`);
            opts = mergeDeep(opts, OptionSets[props.optionSet]);
        }
        if (props.options) {
            opts = mergeDeep(opts, props.options);
            opts._ext_prop_hash = hashObj(props.options);
        }
        opts = validateOptions(opts);
        // inplace mutation
        if (DEBUG_LIMIT) {
            opts.layout.limit = DEBUG_LIMIT;
        }
        return opts;
    }
    static _getInitialState(props) {
        /* calculate everything except stuff that is
    - imageLocs sensitive
    - width/height sensitive
     */ return {
            viewerId: props.viewerId,
            opts: FreeMordor._resolveOptions(props),
            zoom: 500,
            zoomMin: 50,
            // we are processing frames - do not run expensive update ops
            animating: false,
            // we finish initialization after receiving width/height from autosizer
            // states: initializing, zooming, scrolling, moving, resizing
            mode: 'initializing',
            // receiving mouse events for layers (dragging of features)
            layerInteract: false,
            // client geometry
            scrollInfo: {
                scrollWidth: 0,
                scrollHeight: 0,
                scrollLeft: 0,
                scrollTop: 0
            },
            sizeInfo: {
                width: 0,
                height: 0
            },
            missingScrollSpace: null,
            center: {
                x: 0,
                y: 0
            },
            viewport: {
                left: 0,
                top: 0,
                bottom: 0,
                right: 0,
                width: 0,
                height: 0,
                virtualWidth: 0,
                virtualHeight: 0
            },
            // mouse cursor
            cursor: {
                x: 0,
                y: 0,
                position: {
                    x: 0,
                    y: 0
                },
                detectedEnvironment: {
                    isMouseDetected: false,
                    isTouchDetected: false
                },
                isPositionOutside: true
            },
            selected: {
                // staring at something at center
                center: null,
                // hovering something
                hover: null,
                // multi selected by click
                clicked: []
            },
            // hmm, how to hook from debugger into event handlers
            handler: {
                onCursorMove: null
            },
            lastWheelTs: -1000,
            lastWheelCursorTarget: {
                x: 0,
                y: 0
            },
            // rectangular mouse selection
            rectSelect: null,
            // active mouse drag
            dragging: false,
            // copied from props
            viewerFrame: null,
            initialScrollTo: props.initialScrollTo,
            optionSet: props.optionSet
        };
    }
    static checkResetDueOptions(nextOptionSet, prevOptionSet, nextOptions, prevOptions) {
        /* return true if viewer needs full reset due to option change
     */ if (nextOptionSet !== prevOptionSet) {
            return true;
        }
        // ugly fugly
        if (nextOptions) {
            return [
                'zoom.mode',
                'render.paginations',
                'layout.gutter_x',
                'layout.gutter_y'
            ].some((n)=>!isNil(get(nextOptions, n)) && get(nextOptions, n) !== get(prevOptions, n));
        }
        //~ if (nextOptions?.zoom?.mode && nextOptions?.zoom?.mode !== prevOptions.zoom.mode) {
        //~ return true
        //~ }
        return false;
    }
    static getDerivedStateFromProps(nextProps, prevState) {
        let { viewerFrame } = nextProps;
        let nextState = {};
        let st1 = prevState.initialScrollTo;
        let st2 = nextProps.initialScrollTo;
        let initialScrollToChanged = st1.index !== st2.index || st1.id !== st2.id;
        let viewerIdChanged = prevState.viewerId != nextProps.viewerId;
        let imageLocsChanged = prevState.imageLocs != nextProps.imageLocs;
        let initOptionsChanged = FreeMordor.checkResetDueOptions(nextProps.optionSet, prevState.optionSet, nextProps.options, prevState.opts);
        //~ log.x(initialScrollToChanged, st1.index, st2.index, st1.id, st2.id)
        let reset_needed = viewerIdChanged || initialScrollToChanged || initOptionsChanged;
        if (reset_needed) {
            // reset viewer
            nextState = {
                ...nextState,
                ...FreeMordor._getInitialState(nextProps)
            };
            //~ log.debug('reset-opts %o ', {viewerIdChanged, initialScrollToChanged, initOptionsChanged})
            if (prevState.sizeInfo.width > 0) nextState = {
                ...nextState,
                sizeInfo: prevState.sizeInfo
            };
        } else if (viewerFrame && prevState.viewerFrame !== viewerFrame) {
            // processing viewer frames here
            // a go() actions resets the viewerFrame buffer, this is important
            nextState = {
                ...nextState,
                viewerFrame,
                ...FreeMordor.processFrame(nextProps, prevState, viewerFrame)
            };
        } else if (prevState.opts._ext_prop_hash !== hashObj(nextProps.options)) {
            nextState = {
                ...nextState,
                opts: FreeMordor._resolveOptions(nextProps)
            };
        }
        //~ if (prevState.sizeInfo.width == 0) log.x('sizeinfo: %o', prevState.sizeInfo)
        if (imageLocsChanged || reset_needed) {
            // now calculate the stuff that is width/height sensitive
            let { sizeInfo } = prevState;
            let is_valid_size = sizeInfo.width > 0;
            nextState = {
                ...nextState,
                sizeInfo
            };
            let imageLocs = nextProps.imageLocs || [];
            let layout = Layout(getPagesFlat(imageLocs, 0), {
                ...FreeMordor._resolveOptions(nextProps).layout
            });
            let mergedState = {
                ...prevState,
                ...nextState
            };
            let zlayout = FreeMordor._buildZoomLayout(layout, mergedState.zoom, mergedState.viewport, mergedState.opts, mergedState.animating, nextProps.iconfig);
            nextState = {
                ...nextState,
                layout,
                zlayout,
                imageLocs
            };
            mergedState = {
                ...mergedState,
                ...nextState
            };
            //~ log.debug('reset-images %o ', {imageLocsChanged, reset_needed, is_valid_size})
            if (is_valid_size) {
                let { initialScrollTo } = nextProps;
                let kwargs = {
                    layout,
                    zoomOpts: mergedState.opts.zoom,
                    renderOpts: mergedState.opts.render,
                    initialScrollTo,
                    sizeInfo
                };
                let { x, y, zoom } = FreeMordor.calcInitialPosition(kwargs);
                let newViewState = FreeMordor._setCenter(nextProps, mergedState, x, y, zoom, sizeInfo);
                let zoomMin = FreeMordor.calcMinZoom({
                    layout,
                    viewportOpts: mergedState.opts.viewport,
                    sizeInfo
                });
                nextState = {
                    ...nextState,
                    ...newViewState,
                    zoomMin
                };
            }
        }
        return nextState;
    }
    static _buildZoomLayout(layout, zoom, viewport, opts, animating, iconfig) {
        // stuff that does not affect positioning/sizing only rendering
        let roptions = {
            ...opts.quality,
            ...opts.cache,
            ...opts.render
        };
        //~ log.x(viewport.width)
        return FreeMordor.applyZoom(layout, zoom, iconfig.webcache.sizes, viewport, animating, roptions);
    }
    static beforeZoomChange(newZoom, zoomOpts, onZoomChange) {
        if (newZoom > zoomOpts.max || newZoom < zoomOpts.min) {
            newZoom = Math.min(zoomOpts.max, Math.max(zoomOpts.min, newZoom));
        }
        if (onZoomChange) {
            onZoomChange(newZoom);
        }
        return newZoom;
    }
    static translateToViewport(x, y, viewport, zoom) {
        /* translate screen coords to viewport coords */ return {
            x: viewport.left + x / zoom,
            y: viewport.top + y / zoom
        };
    }
    render() {
        let { apiurl, debuggerOn = {}, className, activeLayers, renderController } = this.props;
        //~ pprint(this.props.imageLocs)
        let { zoom, zoomMin, animating, viewport, zlayout, mode, viewerId, opts, selected, dragging, rectSelect, layout } = this.state;
        let controllerElements = [];
        let controlMode = get(opts, 'zoom.control');
        if (controlMode) {
            controllerElements.push(/*#__PURE__*/ _jsx(ZoomControl, {
                mode: controlMode,
                zoom: zoom,
                zoomMin: zoomMin,
                zoomMax: opts.zoom.max,
                setZoom: this.setZoom
            }, "zoomControl"));
        }
        let externalController = callIfFunc(renderController, {
            // for controlling navigation
            layout,
            selected,
            scrollToId: this.scrollToId,
            // for controlling zoom
            zoom,
            zoomMin,
            zoomMax: opts.zoom.max,
            setZoom: this.setZoom
        });
        if (externalController) {
            controllerElements = controllerElements.concat(React.Children.toArray(externalController));
        }
        if (controllerElements.length === 0) {
            controllerElements = null;
        }
        let scrollProps = {};
        if (mode == 'zooming' || mode == 'moving') {
            // these states send new scroll data
            scrollProps = FreeMordor.calcRawScroll(viewport.left, viewport.top, zoom, viewport);
        //~ log('scroll in %s to %o', zoom, scrollProps)
        }
        let onCursorInfo = opts.mouse.on ? this.onCursorInfo : null;
        let onCursorDeactivate = opts.mouse.on ? this.onCursorDeactivate : null;
        let onDrag = opts.mouse.dragScroll ? this.onDrag : null;
        let onDragStart = opts.mouse.dragScroll ? this.onDragStart : null;
        let onDragEnd = opts.mouse.dragScroll ? this.onDragEnd : null;
        let onWheel = opts.mouse.wheelMode ? this.onWheel : null;
        let onRectSelect = opts.selection.selectRect ? this.onRectSelect : null;
        let handlers = {
            onScrollInfo: this.onScrollInfo,
            onResizeInfo: this.onResizeInfo,
            onCursorInfo,
            onCursorDeactivate,
            onDrag,
            onDragStart,
            onDragEnd,
            onWheel,
            onRectSelect
        };
        let { positionHash, renderHash } = hashLayout(zlayout);
        // selection changes classNames on items
        renderHash = hashObj([
            renderHash,
            selected,
            activeLayers
        ]);
        let mouseDragScroll = dragging || opts.mouse.on && opts.mouse.dragScroll && !opts.mouse.dragScrollKey && !rectSelect;
        let optionizedClassName = clsx(className, styles.renderPane, {
            [styles.noScrollBars]: !opts.scrollTo.bar,
            [styles.mouseDragScroll]: mouseDragScroll,
            [styles[opts.mouse.cursorClass]]: !!opts.mouse.cursorClass
        });
        //~ log('render %o: %o %o %o', viewerId, zlayout, positionHash, renderHash)
        if (zlayout.width === Infinity || zlayout.height === Infinity) {
            // such a layout will lead to endless looping
            // triggered with dpi=0 values
            // which are now fallbacked properly to a positive integer in layout construction
            log.error(zlayout);
            return 'bad infinity layout';
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                controllerElements,
                /*#__PURE__*/ _jsx(RenderPane, {
                    viewerId: viewerId,
                    positionHash: positionHash,
                    renderHash: renderHash,
                    list: zlayout.areas,
                    layoutWidth: zlayout.width,
                    layoutHeight: zlayout.height,
                    zoom: zoom,
                    animating: animating,
                    renderArea: this.renderArea,
                    className: optionizedClassName,
                    selectMetaKey: opts.selection.selectRectKey,
                    dragScrollMetaKey: opts.mouse.dragScrollKey,
                    ...handlers,
                    ...scrollProps
                }),
                this.renderRectSelect(),
                this.renderLayersInit(),
                debuggerOn.images && /*#__PURE__*/ _jsx(ControlledImageDebug, {
                    pages: this.state.layout.pages,
                    onClick: ({ otid, zoom })=>this.scrollToId({
                            id: otid,
                            zoom
                        })
                }),
                debuggerOn.internals && /*#__PURE__*/ _jsx(DebugPane, {
                    viewer: this,
                    _recomputes_,
                    _panerrenders_,
                    r: Math.random()
                })
            ]
        });
    }
    constructor(props){
        super(props), _define_property(this, "onScrollInfo", (nextScrollInfo)=>{
            let { zoom, animating, scrollInfo, sizeInfo, mode, opts } = this.state;
            //~ log('onScroll %o', scrollInfo.scrollHeight)
            if (!animating && !isEqual(scrollInfo, nextScrollInfo)) {
                let nextState = FreeMordor.setViewport(this.state, zoom, nextScrollInfo, sizeInfo);
                if (mode != 'initializing') {
                    // we only want to leave initializing after first onResizeInfo event
                    mode = 'scrolling';
                }
                this.setState({
                    ...nextState,
                    scrollInfo: nextScrollInfo,
                    mode: mode
                });
                if (opts.selection.selectCenter && opts.selection.on) {
                    let centerSelect = this.getCenterSelection(nextState.viewport);
                    // we do not clear the center selection if we go from defined -> undefined
                    // this is normally what we want (gutters!), but maybe make this a option
                    if (centerSelect) {
                        this.setSelected({
                            center: centerSelect
                        });
                    }
                }
            }
        }), _define_property(this, "onResizeInfo", (nextSizeInfo)=>{
            let { zoom, scrollInfo, sizeInfo, mode, layout, opts, center } = this.state;
            //~ log(mode, nextSizeInfo)
            if (!isEqual(sizeInfo, nextSizeInfo) && nextSizeInfo.width && nextSizeInfo.height) {
                let nextState = {
                    sizeInfo: nextSizeInfo
                };
                let zoomMin = this.state.zoomMin;
                if (layout.count > 0) {
                    zoomMin = FreeMordor.calcMinZoom({
                        layout,
                        viewportOpts: opts.viewport,
                        sizeInfo: nextSizeInfo
                    });
                    nextState = {
                        ...nextState,
                        zoomMin
                    };
                }
                this.setState(nextState);
                let kwargs = {
                    layout,
                    zoomOpts: opts.zoom,
                    renderOpts: opts.render,
                    sizeInfo: nextSizeInfo,
                    mode
                };
                // keep in min/max range
                zoom = Math.min(Math.max(zoomMin, zoom), opts.zoom.max);
                let restoreInitialMode = opts.resizing.restoreInitialZoom || opts.resizing.restoreInitialPosition;
                if (mode == 'initializing') {
                    // first screen width/height arives - we can initialize the position
                    // XXX: handling width/height ourselves makes initialization twice as hard
                    kwargs.initialScrollTo = this.props.initialScrollTo;
                } else {
                    if (restoreInitialMode) {
                        // zoom recalc in calcInitialPosition needs this
                        kwargs.initialScrollTo = this.props.initialScrollTo;
                    }
                    kwargs.oldZoom = zoom;
                }
                let { x, y, zoom: newZoom } = FreeMordor.calcInitialPosition(kwargs);
                if (mode == 'initializing') {
                    // will set mode = 'moving'
                    this.setCenter(x, y, newZoom, nextSizeInfo);
                    // report initialized if first width/height is available useViewerControl will now start scrolling/zooming
                    callIfFunc(this.props.onInitialized);
                } else if (restoreInitialMode) {
                    if (!opts.resizing.restoreInitialPosition) {
                        // keep old
                        ;
                        [x, y] = [
                            center.x,
                            center.y
                        ];
                    } else if (opts.resizing.restoreInitialPosition == 'x') {
                        // keep the other
                        y = center.y;
                    } else if (opts.resizing.restoreInitialPosition == 'y') {
                        x = center.x;
                    }
                    if (!opts.resizing.restoreInitialZoom) {
                        // keep old (from calcInitialPosition)
                        newZoom = zoom;
                    }
                    this.setCenter(x, y, newZoom, nextSizeInfo);
                } else {
                    // probably min/max range invalidated
                    this.setCenter(center.x, center.y, zoom, nextSizeInfo);
                }
            }
        }), _define_property(this, "setZoom", (zoom)=>{
            this.setState(FreeMordor._setZoom(this.props, this.state, zoom));
        }), _define_property(this, "setCenter", (x, y, newZoom, newSizeInfo)=>{
            this.setState(FreeMordor._setCenter(this.props, this.state, x, y, newZoom, newSizeInfo));
        }), _define_property(this, "buildZoomLayout", (layout, state)=>{
            let { zoom, viewport, opts, animating } = state;
            return FreeMordor._buildZoomLayout(layout, zoom, viewport, opts, animating, this.props.iconfig);
        }), _define_property(this, "getVisible", (viewport)=>{
            let { bush } = this.state.layout;
            if (!viewport) {
                viewport = this.state.viewport;
            }
            let visible = bush.search({
                minX: viewport.left,
                minY: viewport.top,
                maxX: viewport.right,
                maxY: viewport.bottom
            }).map((e)=>e.id);
            return {
                visible
            };
        }), _define_property(this, "getCenterSelection", (newViewport)=>{
            /* return id property of center area
     */ let { layout, viewport } = this.state;
            if (newViewport) {
                viewport = newViewport;
            }
            // make it a point search
            return get(layout.bush.search({
                minX: viewport.left + viewport.width / 2.001,
                minY: viewport.top + viewport.height / 2.001,
                maxX: viewport.right - viewport.width / 2.001,
                maxY: viewport.bottom - viewport.height / 2.001
            })[0], 'id', null);
        }), _define_property(this, "getAreaAtPos", ({ x, y })=>{
            // return id of area at position
            let { layout } = this.state;
            let d = 0.0 // later set to 0, seems to work better without a box (use-case: hover-select)
            ;
            return get(layout.bush.search({
                minX: x + d,
                minY: y + d,
                maxX: x - d,
                maxY: y - d
            })[0], 'id', null);
        }), _define_property(this, "setLayout", (layout)=>{
            this.setState((state)=>{
                if (!layout) {
                    layout = Layout(getPagesFlat(state.imageLocs, 0), {
                        ...state.opts.layout
                    });
                }
                return {
                    layout,
                    zlayout: this.buildZoomLayout(layout, state)
                };
            });
        }), _define_property(this, "_fpsAdaptation", (frame, adaptive)=>{
            //~ return false
            let { ts, i, last, remaining } = frame;
            let { fps } = this.state.opts;
            if (i == 0) {
                this._frameinfo.dropped = 0;
                this._frameinfo.delays.length = 0;
            } else if (adaptive) {
                let FPS_TARGET = 45;
                if (last && this._frameinfo.dropped / i > 0.02) {
                    // reduce FPS on 2% frame drop
                    fps -= 1;
                    log.warn('reducing FPS to %o [dropped %o]', fps, this._frameinfo.dropped);
                    this.setState({
                        fps
                    });
                } else if (last && this._frameinfo.dropped == 0 && fps < FPS_TARGET) {
                    fps += 1;
                    log.warn('increasing FPS to %o', fps);
                    this.setState({
                        fps
                    });
                }
            }
            let frameDuration = 1000 / fps;
            let ts_now = now();
            let frameDelay = ts_now - ts;
            this._frameinfo.delays.push(frameDelay);
            let isDropped = false;
            if (!last && frameDelay > frameDuration) {
                // if we lag one frameDuration, drop frame - but not the last!
                //~ log.warn('dropping frame %o [%d ms, %s]', i, ts_now - ts, newZoom)
                this._frameinfo.dropped += 1;
                isDropped = true;
            }
            return isDropped;
        }), _define_property(this, "setAnimating", (animating)=>{
            let state = this.state;
            if (!animating && state.animating != animating) {
                this.setState({
                    zlayout: this.buildZoomLayout(state.layout, state)
                });
            }
            this.setState({
                animating
            });
        }), _define_property(this, "setOption", (name, value)=>{
            /* this is only used by debugger and should probably use a extra "changed options" tracker
     */ let opts = cloneDeep(this.state.opts);
            if (!Array.isArray(name)) {
                name = [
                    name
                ];
                value = [
                    value
                ];
            }
            each(zip(name, value), ([n, v])=>{
                let [n1, n2] = n.split('.');
                if (n2) {
                    opts[n1][n2] = v;
                } else {
                    opts[n1] = v;
                }
            });
            this.setState({
                opts
            });
            this.setLayout();
            return opts;
        }), _define_property(this, "setSelected", ({ center, hover, clicked })=>{
            let { selected } = this.state;
            let selectedUpdate = {};
            if (hover !== undefined && hover !== selected.hover) {
                selectedUpdate = {
                    ...selectedUpdate,
                    hover
                };
            }
            if (center !== undefined && center !== selected.center) {
                selectedUpdate = {
                    ...selectedUpdate,
                    center
                };
            }
            // XXX: once this has checked wether clicked data has really changed
            // but if we connect this externally to a zoomToArea effect it's better to trigger als
            // with unchanged values (but is this really a selected-click anymore?)
            if (clicked !== undefined) {
                selectedUpdate = {
                    ...selectedUpdate,
                    clicked
                };
            }
            if (size(selectedUpdate) > 0) {
                let nextSelected = {
                    ...selected,
                    ...selectedUpdate
                };
                this.setState({
                    selected: nextSelected
                });
                if (this.props.onSelectionChange) {
                    // emit current change + historically combined state (selected)
                    this.props.onSelectionChange({
                        change: selectedUpdate,
                        selected: nextSelected
                    });
                }
            }
        }), _define_property(this, "onCursorInfo", (cursorInfo)=>{
            /* receiving mouse cursor info
     */ let { cursor, viewport, zoom, handler, opts } = this.state;
            let { on: select, selectHover } = opts.selection;
            let translated = {};
            if (cursor.position) {
                translated = FreeMordor.translateToViewport(cursor.position.x, cursor.position.y, viewport, zoom);
                if (select && selectHover) {
                    let hover = this.getAreaAtPos(translated);
                    this.setSelected({
                        hover
                    });
                }
            }
            // merge into old data
            this.setState({
                cursor: {
                    ...cursor,
                    ...cursorInfo,
                    ...translated
                }
            });
            if (handler.onCursorMove) {
                handler.onCursorMove(translated);
            }
        }), _define_property(this, "onCursorDeactivate", ()=>{
            /* mouse leaves observed area */ if (this.state.selected.hover !== null) {
                this.setSelected({
                    hover: null
                });
            }
        }), _define_property(this, "onDrag", (deltaX, deltaY)=>{
            if (this.state.layerInteract) return;
            let { dragScroll } = this.state.opts.mouse;
            // first check enabled axis
            if (isObject(dragScroll)) {
                let { x, y } = dragScroll;
                deltaX = x ? deltaX : 0;
                deltaY = y ? deltaY : 0;
            }
            // we could calculate this better by using initial mouse pos and current one
            let elem = document.getElementById(`paneCollection_${this.state.viewerId}`);
            elem.scrollLeft += deltaX;
            elem.scrollTop += deltaY;
        }), _define_property(this, "onDragStart", ()=>{
            this.setState({
                dragging: true
            });
        }), _define_property(this, "onDragEnd", ()=>{
            // XXX: nothing to recompute here?
            // onDrag manipulates scrollLeft/scrollTop directly on DOM element
            // i've seen gliches but can't reproduce now
            // this.setState({zlayout: this.buildZoomLayout(this.state.layout, this.state)})
            this.setState({
                dragging: false
            });
        }), _define_property(this, "wheelZoom", (direction)=>{
            //~ log('onWheel %o', deltaY)
            let { zoom, zoomMin, cursor, center, lastWheelTs, lastWheelCursorTarget, layout, sizeInfo, opts } = this.state;
            // adapt: lower zoom changes slower, higher zoom faster
            //~ let adapt = zoom / 2000
            let MAGIC_1 = 20, MAGIC_2 = 1.4, MAGIC_3 = 7;
            let newZoom = Math.min(Math.max(zoomMin, zoom + direction * (zoom / MAGIC_1) ** MAGIC_2), opts.zoom.max);
            if (direction > 0) {
                let startWheelSequence = now() - lastWheelTs > 100;
                let { x, y, cx, cy, step } = lastWheelCursorTarget;
                cx = center.x;
                cy = center.y;
                if (startWheelSequence) {
                    x = cursor.x;
                    y = cursor.y;
                    step = 0;
                    this.setState({
                        lastWheelCursorTarget: {
                            x,
                            y,
                            cy,
                            cy,
                            step
                        }
                    });
                //~ log.debug('new wheel sequence')
                } else {
                    this.setState({
                        lastWheelCursorTarget: {
                            ...lastWheelCursorTarget,
                            step: step + 1
                        }
                    });
                }
                let STEPS = MAGIC_3;
                let newX = cx + (x - cx) / STEPS;
                let newY = cy + (y - cy) / STEPS;
                // restrict to non-virtual viewport
                let { viewport } = this.state;
                newX = Math.min(Math.max(0, newX), viewport.right);
                newY = Math.min(Math.max(0, newY), viewport.bottom);
                //~ this.smoothScrollSeq({
                //~ start: startWheelSequence ? center: null,
                //~ end: {x: newX, y: newY},
                //~ startZoom: startWheelSequence ? zoom: null,
                //~ endZoom: newZoom,
                //~ duration: 0.5,
                //~ fps: 30,
                //~ sequenceStart: startWheelSequence,
                //~ })
                this.setCenter(newX, newY, newZoom);
                // this.setZoom(newZoom)
                this.setState({
                    lastWheelTs: now()
                });
            } else {
                // backward, only zoom out, no center change
                this.setZoom(newZoom);
            }
        }), _define_property(this, "wheelPan", ({ x, y, smooth })=>{
            let { center, layout, viewport } = this.state;
            let next_x = center.x;
            let next_y = center.y;
            x = x || 0;
            y = y || 0;
            // 10 wheel ops (normalized to 1) for one full viewport scroll
            let NUM_STEPS_VIEWPORT = 4;
            let delta_x = viewport.width / NUM_STEPS_VIEWPORT * x;
            let delta_y = viewport.height / NUM_STEPS_VIEWPORT * y;
            // maybe more limiting via vphal? - this interferes with gutter+paginations in horizontal-layout
            // XXX: but is important for thumb-layout panning
            //~ x = Math.min(center.x + delta_x, layout.width_g)
            //~ y = Math.min(center.y + delta_y, layout.height_g)
            let vpwhalf = viewport.width / 2;
            let vphhalf = viewport.height / 2;
            x = Math.min(Math.max(vpwhalf, center.x + delta_x), layout.width_g - vpwhalf);
            y = Math.min(Math.max(vphhalf, center.y + delta_y), layout.height_g - vphhalf);
            if (delta_x) {
                next_x = x;
            }
            if (delta_y) {
                next_y = y;
            }
            if (smooth) {
                // XXX: broken, we have a stream of wheel events
                this.nativeScroll(next_x, next_y);
            //~ this.scrollToXYZ(x, y)
            } else {
                this.setCenter(next_x, next_y);
            }
        }), _define_property(this, "onWheel", (deltaY, e)=>{
            if (Math.abs(deltaY) == 0) {
                return;
            }
            // maybe normalize: https://github.com/basilfx/normalize-wheel
            let direction = deltaY > 0 ? -1 : 1;
            let { wheelMode, wheelModeMeta } = this.state.opts.mouse;
            let mode = wheelMode;
            let metaMode = wheelModeMeta;
            if (wheelModeMeta && (e.shiftKey || e.altKey)) {
                mode = wheelModeMeta;
            }
            if (mode == 'none') {
                // unhandled
                return false;
            } else if (mode == 'zoom') {
                this.wheelZoom(direction);
            } else if (mode.slice(0, 4) == 'panX') {
                direction = direction * 0.4;
                this.wheelPan({
                    x: -direction,
                    smooth: mode != 'panX'
                });
            } else if (mode.slice(0, 4) == 'panY') {
                direction = direction * 0.4;
                this.wheelPan({
                    y: -direction,
                    smooth: mode != 'panY'
                });
            }
            return true // return handled indicator
            ;
        }), _define_property(this, "onRectSelect", (mode, data)=>{
            /* receive rectangular selection info from CursorPosition
     */ if (this.state.layerInteract) return;
            if (mode === 'resize') {
                // during mouse-move + active
                let { x, y, width, height } = data;
                // normalize negative values
                if (width < 0) {
                    x += width;
                    width *= -1;
                }
                if (height < 0) {
                    y += height;
                    height *= -1;
                }
                this.setState({
                    rectSelect: {
                        x,
                        y,
                        width,
                        height
                    }
                });
            } else if (mode === 'start') {
                // starting (probably mouse-down)
                this.setState({
                    rectSelect: null
                });
            } else if (mode == 'end' && this.state.rectSelect) {
                // ignore if no "resize" received
                // ending (probably mouse-up)
                let { rectSelect, viewport, zoom } = this.state;
                let { x, y, width, height } = rectSelect;
                let { x: x1, y: y1 } = FreeMordor.translateToViewport(x, y, viewport, zoom);
                let { x: x2, y: y2 } = FreeMordor.translateToViewport(x + width, y + height, viewport, zoom);
                if (this.state.opts.selection.selectRectZoom) {
                    this.scrollToRect(x1, y1, x2, y2);
                }
                this.setState({
                    rectSelect: null
                });
                if (this.props.onRectSelect) {
                    let { layout } = this.state;
                    // search 2d index for areas/pages
                    let sr = layout.bush.search({
                        minX: x1,
                        minY: y1,
                        maxX: x2,
                        maxY: y2
                    });
                    let areas = sr.map(({ id })=>layout.byId[id]);
                    // transform rectangular selection over layout
                    // via intersection with areas into page list with
                    // local bbox coords relative to page orgwidth
                    let pages = areas.map((a)=>{
                        let r = a.orgWidth / a.width;
                        let orgHeight = a.height * r;
                        let t_x = (x)=>parseInt(Math.min(Math.max(0, x - a.x) * r, a.orgWidth));
                        let t_y = (y)=>parseInt(Math.min(Math.max(0, y - a.y) * r, orgHeight));
                        let res = {
                            otid: a.page.id,
                            x1: t_x(x1),
                            y1: t_y(y1),
                            x2: t_x(x2),
                            y2: t_y(y2)
                        };
                        return res;
                    });
                    this.props.onRectSelect({
                        pages,
                        x1,
                        y1,
                        x2,
                        y2,
                        keyState: data
                    });
                }
            }
        }), _define_property(this, "renderRectSelect", ()=>{
            let { rectSelect } = this.state;
            if (rectSelect) {
                let { x, y, width, height } = rectSelect;
                let style = {
                    left: x,
                    top: y,
                    width,
                    height
                };
                return /*#__PURE__*/ _jsx("div", {
                    className: styles.rectselect,
                    style: style
                });
            }
        }), _define_property(this, "scrollToId", ({ id, keepZoom = true, duration = 1, zoom: nextZoom = null })=>{
            /* scroll to given otid */ let { center, layout, zoom, viewport, sizeInfo, opts } = this.state;
            let { animating, viewerFrame } = this.props;
            let area = layout.byId[id];
            if (!area) {
                log.warn('no area for id', id);
                return;
            }
            // special case: we are already in a animation
            // center from state is probably not calculated
            // so use raw data
            if (animating && viewerFrame) {
                let { x, y } = viewerFrame;
                center = {
                    x,
                    y
                }; // zoom is always correct
            }
            let newZoom = zoom;
            if (nextZoom) {
                newZoom = nextZoom;
            } else if (!keepZoom) {
                // handle aspect-ratio as in areaFit mode in calcInitialPosition
                // XXX: gutter handling is missing (see calcInitialPosition)
                if (sizeInfo.height / sizeInfo.width < area.height / area.width) {
                    newZoom = viewport.height / area.height * zoom;
                } else {
                    newZoom = viewport.width / area.width * zoom;
                }
            }
            let zoomOpts = Math.abs(zoom - newZoom) > 5 ? {
                startZoom: zoom,
                endZoom: newZoom
            } : {};
            zoomOpts = {
                ...zoomOpts,
                duration
            };
            let scrollToMode = opts.scrollTo.mode;
            let endCenter = area.center;
            if (opts.render.paginations !== 'off') {
                // XXX: let's hope there are not many places like this
                // scroll a little bit upward to make space for pagination labels
                //~ endCenter = {...endCenter, y: endCenter.y + opts.layout.gutter_y / 2}
                zoomOpts = {
                    ...zoomOpts,
                    endZoom: zoomOpts.endZoom * Z_PAG_ADJUST
                };
            }
            scrollToMode = this.getAutoScrollToMode(scrollToMode, zoom, zoomOpts.endZoom, {
                start: center,
                end: endCenter
            });
            if (scrollToMode == 'jump') {
                area.center.y += layout.options.gutter_y / 2;
                this.setCenter(endCenter.x, endCenter.y, zoomOpts.endZoom);
            } else if (scrollToMode == 'native') {
                this.nativeScroll(endCenter.x, endCenter.y, zoomOpts.endZoom);
            } else {
                if (scrollToMode == 'scroll' && zoomOpts.endZoom) {
                    zoomOpts.startZoom = zoomOpts.endZoom;
                }
                this.smoothScroll({
                    start: center,
                    end: endCenter,
                    ...zoomOpts
                });
            }
        }), _define_property(this, "scrollToAreaRect", (id, ax1, ay1, ax2, ay2, options)=>{
            /* scroll to area and zoom to fit rectangle given
       by area local coords to screen
    */ let { layout } = this.state;
            let area = layout.byId[id];
            if (area) {
                let f = area.width / area.orgWidth;
                let x1 = area.x + ax1 * f;
                let y1 = area.y + ay1 * f;
                let x2 = area.x + ax2 * f;
                let y2 = area.y + ay2 * f;
                this.scrollToRect(x1, y1, x2, y2, options);
            }
        }), _define_property(this, "scrollToRect", (x1, y1, x2, y2, options)=>{
            /* scroll and zoom to fit rectangle to screen
    options: {scrollToMode, duration}
     */ let { sizeInfo, opts, layout, zoom, center } = this.state;
            if (sizeInfo.width == 0 || sizeInfo.height == 0) {
                log.warn('invalid size [%s]', this.state.mode);
                return;
            }
            let w = x2 - x1;
            let h = y2 - y1;
            let endCenter = {
                x: x1 + w / 2,
                y: y1 + h / 2
            };
            // choose the zoom value with a fully width/height contained rectangle
            let endZoom = Math.min(sizeInfo.width / w, sizeInfo.height / h);
            // respect zoomMin
            let zoomMin = FreeMordor.calcMinZoom({
                layout,
                sizeInfo,
                viewportOpts: opts.viewport
            });
            endZoom = options.keepZoom ? zoom : Math.max(endZoom, zoomMin);
            if (options?.align === 'top') {
                endCenter.y = endCenter.y + (sizeInfo.height / endZoom - h) / 2;
            }
            let scrollToMode = options?.scrollToMode || opts.scrollTo.scrollToMode;
            scrollToMode = this.getAutoScrollToMode(scrollToMode, zoom, endZoom, {
                start: center,
                end: endCenter
            });
            if (scrollToMode == 'jump') {
                this.setCenter(endCenter.x, endCenter.y, endZoom);
            } else if (scrollToMode == 'native') {
                this.nativeScroll(endCenter.x, endCenter.y, endZoom);
            } else {
                let options = {
                    start: center,
                    end: endCenter,
                    startZoom: zoom,
                    endZoom
                };
                if (options.duration) {
                    options = {
                        ...options,
                        duration: options.duration
                    };
                }
                this.smoothScroll(options);
            }
        }), _define_property(this, "scrollToXYZ", (x, y, zoom)=>{
            /*  scroll to center point (x, y) with optional zoom value
     */ let { center, zoom: startZoom, opts } = this.state;
            let zoomOpts = zoom ? {
                startZoom,
                endZoom: zoom
            } : {};
            let scrollToMode = this.getAutoScrollToMode(opts.scrollTo.mode, startZoom, zoom, {
                start: center,
                end: {
                    x,
                    y
                }
            });
            if (scrollToMode == 'jump') {
                this.setCenter(x, y, zoomOpts.endZoom);
            } else if (scrollToMode == 'native') {
                this.nativeScroll(x, y, zoomOpts.endZoom);
            } else {
                if (scrollToMode == 'scroll' && zoomOpts.endZoom) {
                    zoomOpts.startZoom = zoomOpts.endZoom;
                }
                this.smoothScroll({
                    start: center,
                    end: {
                        x,
                        y
                    },
                    ...zoomOpts
                });
            }
        }), _define_property(this, "getAutoScrollToMode", (opt_mode, startZoom, endZoom, distanceOpts = null)=>{
            if (opt_mode == 'auto') {
                if (!endZoom || startZoom == endZoom) {
                    opt_mode = 'native';
                } else {
                    opt_mode = 'zoom';
                }
            }
            if (opt_mode == 'jump' || !distanceOpts) {
                return opt_mode;
            }
            let { viewport, opts } = this.state;
            if (!viewport) {
                return opt_mode;
            }
            let { start, end } = distanceOpts;
            if (!start || !end) {
                return opt_mode;
            }
            let dx = end.x - start.x;
            let dy = end.y - start.y;
            let distance = Math.hypot(dx, dy);
            let viewportDistance = Math.hypot(viewport.width, viewport.height) * opts.scrollTo.jumpDistanceViewports;
            return distance > viewportDistance ? 'jump' : opt_mode;
        }), _define_property(this, "nativeScroll", (x, y, endZoom)=>{
            /* use smooth (less expensive) browser scroll animation
    drawbacks: no duration control, no onEnd callback
    */ let { viewerId, viewport, center, zoom, opts } = this.state;
            if (endZoom && endZoom !== zoom) {
                // first trigger a short zoom operation, then trigger native scroll after last frame
                // XXX: maybe move already some percent into final destination during zoom step?
                this.props.smoothScroll({
                    start: center,
                    end: center,
                    startZoom: zoom,
                    endZoom,
                    fps: opts.fps,
                    viewerId,
                    duration: 0.2,
                    appendNativeScroll: {
                        x,
                        y
                    }
                });
            } else {
                // would be useful: https://github.com/w3c/csswg-drafts/issues/3744
                //~ this.setAnimating(true)
                let elem = document.getElementById(`paneCollection_${viewerId}`);
                if (!elem) {
                // harmful for thumb panel initialization
                //~ this.setCenter(x, y)
                } else {
                    let { scrollLeft, scrollTop } = FreeMordor.calcRawScroll(x - viewport.width / 2, y - viewport.height / 2, zoom, viewport);
                    elem.scroll({
                        top: scrollTop,
                        left: scrollLeft,
                        behavior: 'smooth'
                    });
                }
            }
        }), _define_property(this, "smoothScroll", (options)=>{
            /* start frame generation in saga space
     */ let { opts, viewerId } = this.state;
            this.props.smoothScroll({
                ...options,
                fps: opts.fps,
                viewerId
            });
        }), _define_property(this, "smoothScrollSeq", (options)=>{
            let { opts, viewerId } = this.state;
            this.props.smoothScrollSeq({
                ...options,
                fps: options.fps || opts.fps,
                viewerId
            });
        }), _define_property(this, "morph", (options)=>{
            let { opts, viewerId } = this.state;
            this.props.runMorph({
                ...options,
                fps: opts.fps,
                viewerId
            });
        }), _define_property(this, "realign", (align)=>{
            let { layout, center, opts } = this.state;
            if (opts.layout.alignTransition > 0) {
                let endLayout = Layout(layout.pages, {
                    ...layout.options,
                    align
                });
                let id = this.getCenterSelection();
                let end = id ? endLayout.byId[id].center : null;
                this.morph({
                    start: center,
                    end: end,
                    startLayout: layout,
                    endLayout,
                    duration: opts.layout.alignTransition
                });
            } else {
                this.setOption('layout.align', align);
            }
        }), _define_property(this, "onAreaClick", (area, e)=>{
            let { opts } = this.state;
            if (!opts.selection.selectClickMeta || isMetaKey(e, opts.selection.selectClickMeta)) {
                this.setSelected({
                    clicked: [
                        area.id
                    ]
                });
            }
        }), _define_property(this, "onAreaDoubleClick", (area)=>{
            let { selected, zoom, cursor, opts } = this.state;
            if (!opts.mouse.doubleClick) {
                return;
            }
            if (isObject(opts.mouse.doubleClick)) {
                var { zoom: dbl_zoom, scroll: dbl_scroll } = opts.mouse.doubleClick;
            } else {
                var dbl_zoom = false, dbl_scroll = false;
            }
            if (dbl_zoom && dbl_scroll && selected.hover == area.id) {
                let { x, y } = cursor;
                this.scrollToXYZ(x, y, zoom * 2.0);
            } else if (dbl_scroll) {
                // maybe other behavior
                this.scrollToId({
                    id: area.id
                });
            }
        }), _define_property(this, "registerHandler", ({ onCursorMove })=>{
            let { handler } = this.state;
            if (onCursorMove || onCursorMove === null) {
                this.setState({
                    handler: {
                        ...handler,
                        onCursorMove
                    }
                });
            }
        }), /* ----------------------- */ // RENDER
        /* ----------------------- */ _define_property(this, "renderInfo", (index, key, style, area, zoom, selected)=>{
            let { id, width, height, wc: wc1, orgWidth } = area;
            let border = id == selected.hover ? '2px solid blue' : '1px solid blue';
            return /*#__PURE__*/ _jsxs("div", {
                style: {
                    width,
                    height,
                    border: border,
                    fontSize: '10pt',
                    textAlign: 'center'
                },
                children: [
                    `${id}`,
                    zoom > 100 ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                children: `Webcache: ${wc1}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: `Index: ${index}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: `Orgwidth: ${orgWidth}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: `DPI: ${area.page.dpi}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: `Level: ${area.page.level}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: `Parent: ${area.page.pid}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: `Pagination: ${area.page.pagination || ''}`
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: `Caption: ${area.page.caption || ''}`
                            })
                        ]
                    }) : null
                ]
            });
        }), _define_property(this, "getFittingFontSize", (fontFamily, availableHeight, maxFontSize, numChars, availableWidth)=>{
            if (!numChars) {
                numChars = 0;
            }
            let MAGIC_REDUCE_HEIGHT = 0.5;
            let cacheKey = `${parseInt(availableHeight)}_${numChars}`;
            if (!this.paginationFluidCache) {
                this.paginationFluidCache = {};
            }
            let fs = this.paginationFluidCache[cacheKey];
            if (true || !fs) {
                for (fs of range(1, maxFontSize, 0.2)){
                    let charWidth = getTextWidthCached('0', `${fs.toFixed(1)}pt ${fontFamily}`);
                    if (charWidth > availableHeight * MAGIC_REDUCE_HEIGHT || numChars > 0 && charWidth * numChars > availableWidth) {
                        break;
                    }
                }
                //~ log('cache %o', cacheKey)
                this.paginationFluidCache[cacheKey] = fs;
            }
            return fs;
        }), _define_property(this, "renderPaginationFluid", (area, availableHeight)=>{
            let { pagination, caption } = area.page;
            // prefer caption if its a link
            let text = area.page.link_id ? caption : pagination || caption;
            if (!text) {
                return null;
            }
            // ok, some magic cleanup, everybody needs stuff like this
            if (text.slice(0, 7) == '[Seite ') {
                text = `[${text.slice(7, -1)}]`;
            }
            let MOVE_DOWN = 5 // px
            ;
            let MIN_SIZE = 5;
            let MAX_SIZE = 24;
            let fontFamily = 'arial';
            let fs = this.getFittingFontSize(fontFamily, availableHeight - MOVE_DOWN, MAX_SIZE, text.length, area.width);
            if (fs < MIN_SIZE) {
                return null;
            }
            let style = {
                fontFamily,
                fontSize: `${fs}pt`,
                position: 'absolute',
                top: `${area.height + MOVE_DOWN}px`,
                left: `${area.width / 2 - 20}px`
            };
            return /*#__PURE__*/ _jsx("div", {
                className: styles.pagination,
                style: style,
                children: text
            });
        }), _define_property(this, "renderPaginationCustom", (area, availableHeight)=>{
            /* render pagination with external renderer: renderCustomPaginations())
     */ let { renderCustomPaginations } = this.props;
            let style = {
                position: 'absolute',
                top: `${area.height}px`
            };
            let pagination = renderCustomPaginations ? renderCustomPaginations({
                page: area.page,
                availableHeight,
                availableWidth: area.width
            }) : 'missing renderer';
            return /*#__PURE__*/ _jsx("div", {
                style: style,
                children: pagination
            });
        }), _define_property(this, "renderTreeHead", (area, availableHeight)=>{
            let fontFamily = 'arial';
            let MIN_SIZE = 5;
            let MAX_SIZE = 14;
            let fs = this.getFittingFontSize(fontFamily, availableHeight, MAX_SIZE);
            if (fs < MIN_SIZE) {
                return null;
            }
            let style = {
                top: `-${fs * 1.5}px`,
                fontFamily,
                fontSize: `${fs}pt`
            };
            return /*#__PURE__*/ _jsx("div", {
                className: styles.treeAlignStructHead,
                style: style,
                children: area.page.parent.caption
            });
        }), _define_property(this, "renderLayersInit", ()=>{
            /* once for each enabled layer */ let { activeLayers: layers = [] } = this.props;
            let { opts } = this.state;
            return layers.map((name)=>{
                let { init } = Layers[name];
                if (init) {
                    return init(opts.layers[name]);
                }
                return null;
            });
        }), _define_property(this, "onLayerInteract", (layerInteract)=>this.setState({
                layerInteract
            })), _define_property(this, "renderLayers", (layers, layerOpts, area, zoom)=>{
            /* layers for each area */ return layers.map((name)=>{
                let { enabled, render } = Layers[name];
                let opts = layerOpts[name];
                if (!enabled || enabled({
                    area,
                    zoom,
                    opts
                })) {
                    return render({
                        area,
                        zoom,
                        opts,
                        onLayerInteract: this.onLayerInteract
                    });
                }
                return null;
            });
        }), _define_property(this, "renderTileInfo", (index, key, area)=>{
            let { i, j, width, height } = area;
            let border = '1px solid green';
            let style = {
                width,
                height,
                border: border,
                fontSize: '10pt',
                textAlign: 'center'
            };
            return /*#__PURE__*/ _jsx("div", {
                style: style,
                children: `${i}, ${j}`
            });
        }), _define_property(this, "renderArea", (area, index, isScrolling, key, style)=>{
            if (area.type == 'tile') {
                return this.renderTile(area, index, isScrolling, key, style);
            } else {
                if (area.tiled && this.state.opts.render.mode != 'info') {
                    return null;
                } else {
                    return this.renderPage(area, index, isScrolling, key, style);
                }
            }
        }), _define_property(this, "renderTile", (area, index, isScrolling, key, style)=>{
            let { apiurl } = this.props;
            let { opts } = this.state;
            let { render } = opts;
            let tile = null;
            if (render.mode == 'info') {
                tile = this.renderTileInfo(index, key, area);
            } else {
                let { i, j, width, height, parentArea } = area;
                let { id, wc: wc1 } = parentArea;
                let url = `${apiurl}/image/tiler/${id}/${wc1}/${i}/${j}`;
                tile = /*#__PURE__*/ _jsx("img", {
                    style: {
                        width,
                        height
                    },
                    src: url,
                    importance: "low"
                }, `${id}.${wc1}.${i}.${j}`);
            }
            return /*#__PURE__*/ _jsx("div", {
                style: style,
                onDoubleClick: (e)=>this.onAreaDoubleClick(area.parentArea),
                children: /*#__PURE__*/ _jsx("div", {
                    children: tile
                })
            }, key);
        }), _define_property(this, "renderPage", (area, index, isScrolling, key, style)=>{
            /* render a page area (head + layers + image + pagination)
     */ let { apiurl, marked, activeLayers, iconfig } = this.props;
            let { zoom, animating, selected, layout, opts } = this.state;
            let { render, quality, layout: layoutOpts } = opts;
            //~ log('render %o %o %o', index, quality.placeholder, isScrolling || animating)
            let img = null;
            let { id, page, width, height, wc: wcSize = null, orgWidth, pboxWebcacheRatio } = area;
            // resolve link situation
            let physical_id = page.link_id ?? page.id;
            if (render.mode == 'info') {
                img = this.renderInfo(index, key, style, area, zoom, selected);
            }
            if (isScrolling || animating) {
                if (quality.placeholder == 'autoWidth') {
                    wcSize = zoom < 200 ? 128 : zoom < 1000 ? 304 : 1000;
                } else if (quality.placeholder == 'thumb') {
                    wcSize = 128;
                } else if (quality.placeholder == 'info') {
                    img = this.renderInfo(index, key, style, area, zoom, selected);
                }
            }
            if (wcSize === null) {
                // increase webcache resolution in pbox situation
                let ow = pboxWebcacheRatio ? orgWidth * pboxWebcacheRatio : orgWidth;
                let w = pboxWebcacheRatio ? width * pboxWebcacheRatio : width;
                wcSize = getBestWebcacheFit(w, iconfig.webcache.sizes, ow);
            }
            if (!img) {
                let imgProps = {
                    width: '100%',
                    height: '100%',
                    src: wcUrl(apiurl, wcSize, physical_id),
                    importance: 'low'
                };
                let bgimgProps = null;
                if (quality.zoomLoadPlaceholder == 'thumb') {
                    bgimgProps = {
                        width: '100%',
                        height: '100%',
                        src: wcUrl(apiurl, 128, physical_id),
                        importance: 'high'
                    };
                }
                // unclear 'inherit' - was 'absolute' but did not work with pbox support
                let position = area.pboxCSS ? 'inherit' : 'absolute';
                img = [
                    /*#__PURE__*/ _jsx(ControlledImage, {
                        style: {
                            zIndex: 1,
                            position
                        },
                        ...imgProps
                    }, "fg")
                ];
                if (bgimgProps && (wcSize > 128 || wcSize === 0)) {
                    img.push(/*#__PURE__*/ _jsx(ControlledImage, {
                        style: {
                            zIndex: 0,
                            position
                        },
                        ...bgimgProps
                    }, "bg"));
                }
            }
            if (area.pboxCSS) {
                img = /*#__PURE__*/ _jsx("div", {
                    style: {
                        overflow: 'hidden',
                        width: '100%',
                        height: '100%'
                    },
                    children: /*#__PURE__*/ _jsx("div", {
                        style: area.pboxCSS,
                        children: img
                    })
                });
            }
            let layers = null;
            if (activeLayers) {
                layers = this.renderLayers(activeLayers, opts.layers, area, zoom);
            }
            let pagination = null;
            let { align, gutter_y } = layoutOpts;
            if (render.paginations == 'fluid') {
                let availableHeight = align == 'tree' ? gutter_y * 2 * 0.8 : gutter_y;
                pagination = this.renderPaginationFluid(area, zoom * availableHeight, align);
            } else if (render.paginations == 'custom') {
                pagination = this.renderPaginationCustom(area, gutter_y * zoom);
            }
            let head = null;
            if (align == 'tree') {
                let prev_parent = index > 0 ? layout.areas[index - 1].page.pid : null;
                if (prev_parent != area.page.pid) {
                    let availableHeight = gutter_y * 2;
                    head = this.renderTreeHead(area, zoom * availableHeight);
                }
            }
            let classnames = [
                styles.ipane
            ];
            if (selected.center === area.id) {
                // prefer custom external classname
                classnames.push(opts.render.clsCenter || styles.centerSelect);
            }
            if (selected.hover === area.id) {
                classnames.push(opts.render.clsHover || styles.hoverSelect);
            }
            if (marked === area.id) {
                classnames.push(styles.marked);
            }
            return /*#__PURE__*/ _jsxs("div", {
                style: style,
                onClick: (e)=>this.onAreaClick(area, e),
                onDoubleClick: (e)=>this.onAreaDoubleClick(area, e),
                className: clsx(classnames),
                children: [
                    head,
                    layers,
                    /*#__PURE__*/ _jsx(AccessWarning, {
                        style: {
                            height: '100%',
                            fontSize: '50%',
                            lineHeight: '100%'
                        },
                        id: area.id,
                        awstate: page.aw_state,
                        zoom: zoom,
                        children: img
                    }),
                    pagination
                ]
            }, key);
        });
        this._ignoreNextScrollInfo = false;
        //~ log.debug('construct %o', props.viewerId)
        this._frameinfo = {
            dropped: 0,
            delays: [
                0
            ]
        };
        this.state = FreeMordor._getInitialState(props);
    }
}
/* 2D render pane for image collections
   */ _define_property(FreeMordor, "propTypes", {
    // determines our store key
    viewerId: PropTypes.string.isRequired,
    // tree structure with image/page leafs
    imageLocs: PropTypes.object.isRequired,
    // additional classnames
    className: PropTypes.string,
    // mark this id
    //~ marked: PropTypes.integer,
    // use a predefined option set like "simple"
    optionSet: PropTypes.string,
    // set some options directly
    options: PropTypes.object,
    // list of active layer names
    activeLayers: PropTypes.array,
    // show debugger
    debuggerOn: PropTypes.object,
    // get selection events from render pane (center, hover, clicked)
    onSelectionChange: PropTypes.func,
    // get zoom events
    onZoomChange: PropTypes.func,
    // build external controls
    renderController: PropTypes.func
});
_define_property(FreeMordor, "defaultProps", {
    initialScrollTo: {
        index: 0,
        id: null
    }
});
_define_property(FreeMordor, "storeMap", (root, ownprops)=>({
        viewerFrame: root.data.viewer.viewerFrame[ownprops.viewerId] || null,
        iconfig: root.config.instance,
        apiurl: root.config.apiurl
    }));
_define_property(FreeMordor, "actionMap", {
    smoothScroll,
    smoothScrollSeq,
    runMorph
});
// access refs in Viewer (for connect())
_define_property(FreeMordor, "reduxOptions", {
    forwardRef: true
});
_define_property(FreeMordor, "buildTiles", (tilingAreas)=>{
    let tilesize = 1024;
    let tiles = [];
    for (let area of tilingAreas){
        // do not render anymore
        area.tiled = true;
        let imgwidth = area.wc == 0 ? area.orgWidth : area.wc;
        let zratio = area.width / imgwidth;
        let ztilesize = tilesize * zratio;
        let xs = range(0, area.width, ztilesize);
        let ys = range(0, area.height, ztilesize);
        let last_i = xs.length - 1;
        let last_j = ys.length - 1;
        for (let [i, x] of xs.entries()){
            for (let [j, y] of ys.entries()){
                tiles.push({
                    type: 'tile',
                    x: area.x + x,
                    y: area.y + y,
                    i,
                    j,
                    width: i < last_i ? ztilesize : area.width % ztilesize,
                    height: j < last_j ? ztilesize : area.height % ztilesize,
                    parentArea: area
                });
            }
        }
    }
    return tiles;
});
_define_property(FreeMordor, "getPageBoxCSS", (area)=>{
    /* calc CSS vals for pbox on zArea
    stored as .pboxCSS
    */ // we should probably precalc this in zlayout-gen
    // and handle unmodified case (0, 0, orgWidth, orgHeight)
    let [orgWidth, orgHeight] = area.page.size;
    let { orgWidth: orgBoxWidth, width: areaWidth } = area;
    let cropRatio = areaWidth / orgBoxWidth;
    return {
        pboxWebcacheRatio: orgWidth / orgBoxWidth,
        pboxCSS: {
            width: `${orgWidth * cropRatio}px`,
            height: `${orgHeight * cropRatio}px`,
            marginTop: `-${area.page.pbox.top * cropRatio}px`,
            marginLeft: `-${area.page.pbox.left * cropRatio}px`
        }
    };
});
_define_property(FreeMordor, "applyZoom", (layout, zoom, sizes, viewport, animating, roptions)=>{
    // we translate all coordinates by a half viewport to get a "virtual" area for scrolling
    // this has the nice mathematical property, that the translated scroll coords are
    // center of the untranslated (the real) layout, so we need one operation less for getting
    // the real center from scroll info
    // Dirk: ok, the above text is only valid, if viewport.virtual would be constant 0.5 (instead of configurable)
    // vpvw, and vpvh would not be needed and we would have less addition-ops (in other places and here)
    let vpvw = viewport.virtualWidth;
    let vpvh = viewport.virtualHeight;
    let { devicePixelRatio } = roptions;
    let ratio = 1;
    if (devicePixelRatio) {
        ratio = zoom > 128 ? windowDevicePixelRatio : 1;
    }
    let tilingAreas = [];
    //~ let is_intersect = (x1, y1, x2, y2) => {
    //~ if (viewport.left > x2 || viewport.right < x1) {
    //~ return false
    //~ } else if (viewport.top > y2 || viewport.bottom < y1) {
    //~ return false
    //~ }
    //~ return true
    //~ }
    let areas = [];
    for (const area of layout.areas){
        let { width, height, x, y, orgWidth } = area;
        //~ let wc = FreeMordor.getBestWebcacheFit(width * zoom * ratio, orgWidth, sizes)
        let zArea = {
            ...area,
            width: width * zoom,
            height: height * zoom,
            x: (x + vpvw) * zoom,
            y: (y + vpvh) * zoom
        };
        //~ if (zArea.y > 10 ** 7 || zArea.x > 10 ** 7) {
        //~ log.warn('corrupt y: %ox%o', zArea.x, zArea.y)
        //~ }
        if (false && !animating && layout.options.tiles) {
            if (wc > 2000 || wc == 0 && zArea.orgWidth > 3000) {
                // XXX: and zArea is at viewport
                let x1 = x, y1 = y, x2 = x + width, y2 = y + height;
                // XXX: this optimization is not correct on simple scroll
                //~ if (true || is_intersect(x1, y1, x2, y2)) {
                //~ log('pushing %o', zArea.id)
                tilingAreas.push(zArea);
            //~ }
            }
        }
        if (area.page.pbox) {
            zArea = {
                ...zArea,
                ...FreeMordor.getPageBoxCSS(zArea)
            };
        }
        areas.push(zArea);
    }
    let tiles = FreeMordor.buildTiles(tilingAreas);
    let { width, height, id } = layout;
    let zoptions = {
        zoom,
        viewport
    };
    return {
        ...layout,
        zoptions,
        roptions,
        areas: [
            ...areas,
            ...tiles
        ],
        width: (width + vpvw * 2) * zoom,
        height: (height + vpvh * 2) * zoom
    };
});
_define_property(FreeMordor, "calcCenter", ({ zoom, scrollInfo, sizeInfo, viewportOpts })=>{
    let { virtualX, virtualY } = viewportOpts;
    return {
        x: (scrollInfo.scrollLeft + sizeInfo.width / 2 - sizeInfo.width * virtualX) / zoom,
        y: (scrollInfo.scrollTop + sizeInfo.height / 2 - sizeInfo.height * virtualY) / zoom
    };
});
_define_property(FreeMordor, "calcViewport", ({ zoom, center, sizeInfo, viewportOpts })=>{
    let width = sizeInfo.width / zoom;
    let height = sizeInfo.height / zoom;
    let w = width / 2;
    let h = height / 2;
    let { virtualX, virtualY } = viewportOpts;
    let virtualWidth = width * virtualX;
    let virtualHeight = height * virtualY;
    return {
        left: center.x - w,
        top: center.y - h,
        right: center.x + w,
        bottom: center.y + h,
        width,
        height,
        virtualWidth,
        virtualHeight,
        virtualRight: center.x + w + virtualWidth,
        virtualBottom: center.y + h + virtualHeight
    };
});
_define_property(FreeMordor, "calcRawScroll", (x, y, zoom, viewport)=>({
        scrollLeft: (x + viewport.virtualWidth) * zoom,
        scrollTop: (y + viewport.virtualHeight) * zoom
    }));
_define_property(FreeMordor, "calcInitialPosition", ({ layout, zoomOpts, renderOpts, initialScrollTo, sizeInfo, oldZoom })=>{
    let pos = {
        x: 0,
        y: 0,
        zoom: 500
    };
    let { width, height } = sizeInfo;
    if (layout.areas.length == 0) {
        return pos;
    }
    let zoom = (area)=>{
        /* calculate zoom value for current client width/height
       */ let { mode, initialAlign } = zoomOpts;
        let paginations_active = renderOpts.paginations !== 'off';
        // add gutter_y space to height if paginations active
        let g_adapt = (h)=>paginations_active ? h + layout.options.gutter_y : h;
        // compare layout/area aspect ratio to viewport aspect ratio
        let vp_ratio = height / width;
        if (mode == 'layoutFit') {
            mode = vp_ratio < g_adapt(layout.height) / layout.width ? 'layoutHeight' : 'layoutWidth';
        } else if (area && mode == 'areaFit') {
            mode = vp_ratio < g_adapt(area.height) / area.width ? 'areaHeight' : 'areaWidth';
        }
        let SCROLLBAR = 0;
        let res = {
            zoom: oldZoom || zoomOpts.default
        };
        if (area && mode == 'areaHeight' && initialScrollTo) {
            // fit height
            res.zoom = parseInt(height / area.height);
        } else if (area && mode == 'areaWidth' && initialScrollTo) {
            // fit width
            res.zoom = parseInt(width / area.width);
            if (initialAlign == 'top') {
                // align to top section of area
                res.y = area.y + height / res.zoom / 2;
            } else if (initialAlign == 'center') {
                res.y = area.y + area.height / 2;
            }
        } else if (mode == 'layoutHeight') {
            // zoom a little bit out for giving space to paginations
            let z_adjust = paginations_active ? Z_PAG_ADJUST : 1;
            res.zoom = parseInt((height - SCROLLBAR) / layout.height) * z_adjust;
            if (!area) {
                res.y = layout.height / 2;
                if (layout.options.align == 'square') {
                    // ok, we choose the layout center
                    res.x = layout.width / 2;
                } else {
                    res.x = width / res.zoom / 2;
                }
            }
        } else if (mode == 'layoutWidth') {
            res.zoom = parseInt((width - SCROLLBAR) / layout.width);
            if (area) {
                if (layout.options.align.startsWith('vertical')) {
                    // align to top section of area
                    res.y = area.y + height / res.zoom / 2;
                }
            } else {
                // no area - center of layout
                res.x = layout.width / 2;
                // align top
                res.y = height / res.zoom / 2;
            }
        }
        return res;
    };
    // choose center
    let area = null;
    if (initialScrollTo && initialScrollTo.index) {
        area = layout.areas[initialScrollTo.index];
    } else if ([
        'areaHeight',
        'areaWidth',
        'areaFit'
    ].includes(zoomOpts.mode)) {
        area = layout.areas[0];
    }
    if (area) {
        pos = {
            ...pos,
            x: area.center.x,
            y: area.center.y
        };
    }
    pos = {
        ...pos,
        ...zoom(area)
    };
    // XXX: MAGIC_2 is not a good idea, vertical-layout + areaWidth will not fit exactly
    //~ let MAGIC_2 = 0.66 // move 2/3 of gutter_y upward
    //~ if (['layoutHeight', 'areaHeight'].includes(zoomOpts.mode) && pos.y) {
    //~ pos = {...pos, y: pos.y + layout.options.gutter_y * MAGIC_2}
    //~ }
    return pos;
});
_define_property(FreeMordor, "setViewport", (prevState, zoom, scrollInfo, sizeInfo, center)=>{
    let { viewport: viewportOpts } = prevState.opts;
    if (!center) {
        center = FreeMordor.calcCenter({
            zoom,
            scrollInfo,
            sizeInfo,
            viewportOpts
        });
    }
    let viewport = FreeMordor.calcViewport({
        zoom,
        center,
        sizeInfo,
        viewportOpts
    });
    let nextState = {
        center,
        viewport
    };
    return nextState;
});
_define_property(FreeMordor, "_setCenter", (props, state, x, y, newZoom, newSizeInfo)=>{
    let { zoom, sizeInfo, layout, scrollInfo, animating, opts } = state;
    let { iconfig, onZoomChange } = props;
    let center = {
        x,
        y
    };
    if (newSizeInfo) {
        sizeInfo = newSizeInfo;
    }
    if (newZoom) {
        newZoom = FreeMordor.beforeZoomChange(newZoom, opts.zoom, onZoomChange);
        let nextState = FreeMordor.setViewport(state, newZoom, scrollInfo, sizeInfo, center);
        let zlayout = FreeMordor._buildZoomLayout(layout, newZoom, nextState.viewport, opts, animating, iconfig);
        return {
            ...nextState,
            zlayout,
            mode: 'moving',
            zoom: newZoom
        };
    } else {
        let nextState = FreeMordor.setViewport(state, zoom, scrollInfo, sizeInfo, center);
        return {
            ...nextState,
            mode: 'moving'
        };
    }
});
_define_property(FreeMordor, "calcMinZoom", ({ layout, sizeInfo, viewportOpts })=>{
    /* restrict min zoom level by available viewport size and by layout geometry
    XXX: maybe make this optional/overridable
    XXX: this collides with the opts.zoom.min eval in beforeZoomChange
     */ let { virtualX, virtualY, allowZoomInvalidate } = viewportOpts;
    if (allowZoomInvalidate) {
        virtualX = virtualY = 10;
    }
    // XXX: does this makes sens, this will always go negative
    let minZoomX = (sizeInfo.width - sizeInfo.width * virtualX) / layout.width;
    let minZoomY = (sizeInfo.height - sizeInfo.height * virtualY) / layout.height;
    return Math.max(minZoomX, minZoomY, MIN_ZOOM);
});
_define_property(FreeMordor, "_setZoom", (props, state, zoom)=>{
    let { sizeInfo, center, layout, scrollInfo, opts } = state;
    let { onZoomChange } = props;
    zoom = FreeMordor.beforeZoomChange(zoom, opts.zoom, onZoomChange);
    let nextState = FreeMordor.setViewport(state, zoom, scrollInfo, sizeInfo, center);
    nextState = {
        ...nextState,
        zoom,
        mode: 'zooming'
    };
    nextState = {
        ...nextState,
        zlayout: FreeMordor._buildZoomLayout(layout, nextState.zoom, nextState.viewport, opts, nextState.animating, props.iconfig)
    };
    return nextState;
});
_define_property(FreeMordor, "processFrame", (props, state, frame)=>{
    let { x, y, zoom: newZoom, layout, last, nativeSmoothScroll } = frame;
    let { animating, zoom, viewerId, viewport, opts } = state;
    let nextState = {};
    if (last) {
        //~ log('last frame of %o [%s]', frame.animationId, frame.i)
        nextState.animating = false;
    } else if (!animating) {
        nextState.animating = true;
    }
    let isDropped = false // this._fpsAdaptation(frame, opts.quality.adaptiveFps)
    ;
    if (!isDropped) {
        //~ log.info('rendering %o [%d ms]', i, remaining)
        let FAST_PATH = !last // last frame should set correct center again
        ;
        if (!layout && (!newZoom || zoom == newZoom) && FAST_PATH) {
            //~ log.info('fast path %o', viewerId)
            let elem = document.getElementById(`paneCollection_${viewerId}`);
            if (elem) {
                let { scrollLeft, scrollTop } = FreeMordor.calcRawScroll(x - viewport.width / 2, y - viewport.height / 2, zoom, viewport);
                if (!nativeSmoothScroll) {
                    elem.scrollLeft = scrollLeft;
                    elem.scrollTop = scrollTop;
                } else {
                    elem.scroll({
                        top: scrollTop,
                        left: scrollLeft,
                        behavior: 'smooth'
                    });
                }
            } else {
                log.warn('no dom node with id %o found', `paneCollection_${viewerId}`);
            }
        } else {
            // seen during hotreload (state.layout == null)
            if (state.layout) {
                //~ log.info('slow path')
                // this is slower ...
                // but has correct state updates
                // and its required for zoom changes
                if ((x || x === 0) && (y || y === 0)) {
                    nextState = {
                        ...nextState,
                        ...FreeMordor._setCenter(props, state, x, y, newZoom)
                    };
                } else if (newZoom) {
                    nextState = {
                        ...nextState,
                        ...FreeMordor._setZoom(props, state, newZoom)
                    };
                }
            }
        }
    }
    return nextState;
});
// withRef gives us `vl.parts.ViewerGrid.mordor` from chrome console
export let Mordor = connect('Mordor', FreeMordor);
